import { ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';

/**
 * Borderless list for the portal: hairlines between rows, top and bottom
 * included, so the list sits on the paper rather than in a box.
 */
interface PortalListProps extends React.ComponentProps<'ul'> {
  /**
   * Fade rows in with a short stagger. Set only when the list replaces a
   * skeleton; cached data on route change should just be there.
   */
  animateEnter?: boolean;
}

export function PortalList({
  animateEnter = false,
  children,
  ...props
}: PortalListProps) {
  return (
    <ul
      className="divide-border divide-y border-y"
      data-enter={animateEnter || undefined}
      {...props}
    >
      {children}
    </ul>
  );
}

interface PortalListRowProps extends Omit<LinkProps, 'className'> {
  /** Grid template for the row content; every row in a list shares one. */
  className: string;
  children: ReactNode;
  /** 1-based position in the list; drives the enter stagger delay when the list animates. */
  index?: number;
  /** A control after the link, such as a button: outside it, so it is its own target. */
  action?: ReactNode;
}

/**
 * A 48px row whose whole surface is the link. The row lays its children out
 * on a grid supplied by the caller and ends with the hover chevron, or with
 * its `action`.
 */
export function PortalListRow({
  children,
  className,
  index,
  action,
  ...props
}: PortalListRowProps) {
  return (
    <li
      className={cn('px-2', action && 'flex items-center gap-3')}
      style={
        index === undefined
          ? undefined
          : ({ '--portal-row-index': index } as React.CSSProperties)
      }
    >
      <Link
        className={cn(
          'group hover:bg-accent focus-visible:bg-accent focus-visible:ring-ring/50 -mx-2 grid min-h-12 items-center gap-3 rounded-md px-2 py-1.5 transition-colors duration-100 outline-none focus-visible:ring-[3px] active:bg-neutral-100',
          action && 'min-w-0 flex-1',
          className,
        )}
        {...props}
      >
        {children}
        <ChevronRight
          aria-hidden
          className="group-hover:text-muted-foreground size-4 self-start justify-self-end text-neutral-300 transition-[color,transform] duration-150 ease-out [@media(hover:hover)]:group-hover:translate-x-0.5"
        />
      </Link>
      {action}
    </li>
  );
}

const VISIBLE_ROWS = 3;
const EXPAND_STEP = 10;

interface PortalListLimitProps<T> {
  items: T[];
  /** Renders the rows to show: the first three, then more on request. */
  children: (visible: T[]) => ReactNode;
}

/** A list that starts with its first three rows, with "Show more" and "Show fewer" under it. */
export function PortalListLimit<T>({
  items,
  children,
}: PortalListLimitProps<T>) {
  const [visibleCount, setVisibleCount] = useState(VISIBLE_ROWS);
  const hiddenCount = Math.max(items.length - visibleCount, 0);
  const isExpanded = visibleCount > VISIBLE_ROWS;

  return (
    <>
      {children(items.slice(0, visibleCount))}

      {(hiddenCount > 0 || isExpanded) && (
        <div className="flex items-center gap-3 text-xs font-medium">
          {hiddenCount > 0 && (
            <button
              className="hover:text-foreground cursor-pointer text-neutral-500"
              onClick={() => setVisibleCount((count) => count + EXPAND_STEP)}
              type="button"
            >
              Show {Math.min(hiddenCount, EXPAND_STEP)} more
              {hiddenCount > EXPAND_STEP && ` · ${hiddenCount} hidden`}
            </button>
          )}
          {isExpanded && (
            <button
              className="hover:text-foreground cursor-pointer text-neutral-500"
              onClick={() => setVisibleCount(VISIBLE_ROWS)}
              type="button"
            >
              Show fewer
            </button>
          )}
        </div>
      )}
    </>
  );
}

interface PortalListSkeletonProps {
  rows: number;
  /** Two-line rows (name over a secondary line) are taller than one-line rows. */
  lines?: 1 | 2;
}

/**
 * Mirrors the loaded list so the page does not restructure when data arrives:
 * same row padding, same 20px line boxes, so a one-line row is 48px and a
 * two-line row is 53px, exactly like `PortalListRow`.
 */
export function PortalListSkeleton({
  rows,
  lines = 1,
}: PortalListSkeletonProps) {
  return (
    <div
      aria-label="Loading"
      className="divide-border divide-y border-y"
      role="status"
    >
      {Array.from({ length: rows }, (_, index) => (
        <div className="flex min-h-12 items-center gap-3 py-1.5" key={index}>
          <Skeleton className="size-6 shrink-0 rounded-full bg-neutral-200" />
          <div className="flex flex-col gap-px">
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-48 max-w-full bg-neutral-200" />
            </div>
            {lines === 2 && (
              <div className="flex h-5 items-center">
                <Skeleton className="h-3 w-36 max-w-full bg-neutral-200" />
              </div>
            )}
          </div>
          <Skeleton className="hidden h-3.5 w-40 bg-neutral-200 md:block" />
        </div>
      ))}
    </div>
  );
}

/** Quiet single line in place of a list: empty and all-clear states. */
export function PortalListNote({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground border-y py-4 text-[13px]">
      {children}
    </p>
  );
}
